import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { formatDisplayUserId } from "../utils/userIdFormat";
import UserActionsDropdown from "../components/UserActionsDropdown";
import TransactionModal from "../components/TransactionModal";
import Loading from "../components/Loading";
import ClientExposureModal from "../components/ClientExposureModal";
import BlockActionModal from "../components/BlockActionModal";
import PartnershipModal from "../components/PartnershipModal";
import { USER_TYPES } from "../store/actions/userManagement";
import { useToggleUserStatusMutation } from "../services/auth/authApi";
import { getDomainAppUrl } from "../utils/domainConfig";
import { getCurrentUserLevel, USER_LEVELS } from "../config/menuItems";

// Icons
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
  </svg>
);

const MenuIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M7 10l5 5 5-5z" />
  </svg>
);

// WhatsApp Icon
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

// Eye Icon
const EyeIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M942.2 486.2C847.4 286.5 704.1 186 512 186c-192.2 0-335.4 100.5-430.2 300.3a60.3 60.3 0 000 51.5C176.6 737.5 319.9 838 512 838c192.2 0 335.4-100.5 430.2-300.3 7.7-16.2 7.7-35 0-51.5zM512 766c-161.3 0-279.4-81.8-362.7-254C232.6 339.8 350.7 258 512 258c161.3 0 279.4 81.8 362.7 254C791.5 684.2 673.4 766 512 766zm-4-430c-97.2 0-176 78.8-176 176s78.8 176 176 176 176-78.8 176-176-78.8-176-176-176zm0 288c-61.9 0-112-50.1-112-112s50.1-112 112-112 112 50.1 112 112-50.1 112-112 112z"></path>
  </svg>
);

const DownlineListPage = ({ userType, title, customData, isLoading, paginationData }) => {
  console.log('🟢 DownlineListPage - userType:', userType,USER_TYPES.CLIENT);
  const navigate = useNavigate();
  const location = useLocation();
  // Passwords are only ever shown in plaintext to the top-level Admin —
  // every other level sees a masked value and loses the WhatsApp-share button.
  const isAdminViewer = getCurrentUserLevel() === USER_LEVELS.ADMIN;
  const [currentPage, setCurrentPage] = useState(1);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [transactionModal, setTransactionModal] = useState({
    isOpen: false,
    type: null,
    user: null,
  });
  const [passwordModal, setPasswordModal] = useState({ isOpen: false, user: null });
  const [partnershipModal, setPartnershipModal] = useState({ isOpen: false, userId: null, mongoId: null });
  const [clientExposureModal, setClientExposureModal] = useState({ isOpen: false, user: null });
  const [blockActionModal, setBlockActionModal] = useState({ isOpen: false, user: null });
  const [displayData, setDisplayData] = useState([]);
  const [toggleUserStatus] = useToggleUserStatusMutation();

  // Track URL changes and update display data
  useEffect(() => {
    // Clear data when URL changes
    setDisplayData([]);
    
    // Set new data when available and not loading
    if (customData && !isLoading) {
      setDisplayData(customData);
    }
  }, [location.pathname, customData, isLoading]);

  // Mapping from API response userType string to next level user type name
  const userTypeObj = {
    "Admin": "super admin",
    "SuperAdmin": "mini admin",
    "MiniAdmin": "master",
    "Master": "super master",
    "Super": "agent",
    "Agent": "client"
  };

  // Transform data to match table structure
  const transformedUsers = displayData?.map(user => {
    return {
      id: user.id || user._id,
      userId: user.userId,
      userName: user.username,
      userType: user.userType,
      password: user.password,
      parentId: user.parentId ? {
        _id: user.parentId._id,
        userId: user.parentId.userId,
        username: user.parentId.username,
        userType: user.parentId.userType
      } : null,
      fullName: user.fullName,
      contact: user.contactNo,
      isActive: user.isActive,
      parentName: user.parentId?.fullName,
      createdOn: user.createdAt,
      partnerShip: user.shares?.myShare || 0,
      matchCommission: user.shares?.matchCommission || 0,
      sessionCommission: user.shares?.sessionCommission || 0,
      casinoShare: user.shares?.casinoShare || 0,
      casinoCommission: user.shares?.casinoCommission || 0,
      matkaCommission: user.shares?.matkaCommission || 0,
      balance: user.availableBalance ?? user.balance ?? 0,
      exposureLimit: user.exposureLimit || 0,
      liability: user.exposure || 0
    };
  }) || [];

  // Check if users are clickable
  const hasLowerLevel = transformedUsers.length > 0 && transformedUsers[0].userType !== 'Client';
  const isClickable = hasLowerLevel;
  
  // Check if displaying Client-level users to show Exposure column
  const isDisplayingClients = transformedUsers.length > 0 && transformedUsers[0].userType === 'Client';

  // Get user type key for create/update links
  const getUserTypeKey = () => {
    if (transformedUsers.length === 0) return 'user';
    
    const userTypeMapping = {
      'SuperAdmin': 'superadmin',
      'MiniAdmin': 'miniadmin',
      'Master': 'master',
      'Super': 'supermaster',
      'Agent': 'agent',
      'Client': 'client'
    };
    
    return userTypeMapping[transformedUsers[0].userType] || 'user';
  };

  // Handle user click for navigation
  const handleUserClick = (user) => {
    const adminId = user.id;
    const nextLevelName = userTypeObj[user.userType];
    
    if (!adminId) {
      return;
    }

    navigate(`/admin-details/downline-by-id/${adminId}/${user.userType}/${nextLevelName}`);
  };

  const formatUserId = (userId) => {
    return formatDisplayUserId(userId);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handlePartnershipClick = (user) => {
    setPartnershipModal({ isOpen: true, userId: user.userId, mongoId: user.id });
  };

  const handleClosePartnership = () => {
    setPartnershipModal({ isOpen: false, userId: null, mongoId: null });
  };

  const handleDropdownClick = (e, userId) => {
    e.stopPropagation();
    if (activeDropdown === userId) {
      setActiveDropdown(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
      setActiveDropdown(userId);
    }
  };

  const handleCloseDropdown = () => {
    setActiveDropdown(null);
  };

  const handleAction = async (action, user) => {
    handleCloseDropdown();
    
    switch (action) {
      case "deposit":
        setTransactionModal({ isOpen: true, type: "deposit", user: user });
        break;
      case "withdraw":
        setTransactionModal({ isOpen: true, type: "withdraw", user: user });
        break;
      case "toggleStatus":
        try {
          const userId = user.id || user._id;
          await toggleUserStatus(userId).unwrap();
        } catch (err) {
          console.error('Failed to toggle user status:', err);
        }
        break;
      case "blockMarket":
        setBlockActionModal({ isOpen: true, user: user });
        break;
      case "edit":
        { const editUserId = user.id || user._id || user.userId;
        navigate("/admin-details/edit/" + editUserId);
        break; }
      case "statement":
        { const statementUserId = user.id || user._id || user.userId;
        navigate("/admin-details/statement/" + statementUserId);
        break; }
      case "accountOps":
        { const accountOpsUserId = user.id || user._id || user.userId;
        navigate("/admin-details/account-operations/" + accountOpsUserId);
        break; }
      case "loginReport":
        { const loginReportUserId = user.id || user._id || user.userId;
        navigate("/admin-details/login-report/" + loginReportUserId);
        break; }
      case "commissionReport":
        { const commUserId = user.id || user._id;
        navigate("/ledger/comm-lenden?userId=" + commUserId);
        break; }
      case "resetPassword":
        console.log('Reset password for user:', user);
        // Add reset password modal logic here
        break;
      default:
        break;
    }
  };

  return (
    <div className="w-full">
      <div className="bg-white shadow rounded">
        {/* Header */}
        <div className="bg-[#0F2327] px-5 py-3 flex items-center justify-between">
          <span className="text-xl text-white font-normal">
            {title}
          </span>
          <button
            onClick={handleBack}
            className="bg-blue-500 text-white px-4 py-1 rounded hover:bg-blue-600"
          >
            Back
          </button>
        </div>

        {/* Action Buttons */}
        <div className="px-3 py-3 flex justify-around gap-4">
          <Link
            to={"/admin-details/create/" + getUserTypeKey()}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            + Create
          </Link>
          <Link
            to={"/admin-details/update-limit/" + getUserTypeKey()}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Update Limit
          </Link>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {isLoading || displayData.length === 0 ? (
            isLoading ? (
              <div className="flex justify-center items-center min-h-[400px]">
                <Loading />
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                No users found
              </div>
            )
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#0F2327] text-white border-y">
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">#</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Action</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">User</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Parent</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Password</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Share</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-center" colSpan="4">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Commission %</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Coins</span>
                      </div>
                    </span>
                  </th>
                  {isDisplayingClients && (
                    <th className="p-2 border text-left" rowSpan="2">
                      <span className="ant-table-header-column">
                        <div>
                          <span className="ant-table-column-title">Exposure</span>
                        </div>
                      </span>
                    </th>
                  )}
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Status</span>
                      </div>
                    </span>
                  </th>
                </tr>
                <tr className="bg-[#0F2327] text-white border-y">
                  <th className="p-2 border text-center">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Match Comm</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-center">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Sess Comm</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-center">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Casino Comm</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-center">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Matka Comm</span>
                      </div>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {transformedUsers.map(function (user, index) {
                  const rowBgColor = index % 2 === 0 ? "var(--row-even)" : "var(--row-odd)";
                  return (
                    <tr
                      key={user.id || index}
                      style={{ backgroundColor: rowBgColor }}
                    >
                      <td className="p-2 border">
                        <button
                          onClick={() => handlePartnershipClick(user)}
                          className="bg-blue-500 text-white p-1 rounded"
                          title="View Partnership"
                        >
                          <PlusIcon />
                        </button>
                      </td>
                      <td className="p-2 border">
                        <button
                          onClick={(e) => handleDropdownClick(e, user.userId)}
                          className="bg-blue-500 text-white p-1 rounded"
                        >
                          <MenuIcon />
                        </button>
                      </td>
                      <td className="p-2 border">
                        {isClickable ? (
                          <a
                            href="#"
                            className="text-blue-600 hover:text-blue-800 hover:underline text-xs flex items-center justify-start"
                            onClick={function (e) {
                              e.preventDefault();
                              handleUserClick(user);
                            }}
                          >
                            <EyeIcon />
                            <span style={{paddingLeft:"5px"}} className="whitespace-nowrap">
                            {user.userId} ({user.fullName})
                            </span>
                          </a>
                        ) : (
                          <span className="text-xs text-gray-600 whitespace-nowrap">{user.userId} ({user.fullName})</span>
                        )}
                      </td>
                      <td className="p-2 border">
                        {user.parentName || user.parentId?.fullName || '-'}
                        {user.parentId?.userId && (
                          <span className="text-xs text-gray-500 ml-2">({user.parentId.userId})</span>
                        )}
                      </td>
                      <td className="p-2 border">
                        <div className="flex items-center justify-between gap-2">
                          <span>{isAdminViewer ? (user.password || '***') : '••••••••'}</span>
                          {isAdminViewer && (
                          <button
                            className="bg-green-500 text-white px-2 py-1 text-xs rounded hover:bg-green-600 flex items-center gap-1"
                            onClick={function() {
                              const SUBDOMAIN_MAP = {
                                client: '', agent: 'admin', super: 'admin',
                                master: 'admin', miniadmin: 'admin',
                                superadmin: 'admin', admin: 'admin',
                              };
                              const baseDomain = getDomainAppUrl();
                              const clientDomain = `https://${baseDomain}`;
                              const userTypeKey = String(user.userType || '').toLowerCase();
                              const sub = SUBDOMAIN_MAP[userTypeKey];
                              const domain = sub === undefined || sub === ''
                                ? clientDomain
                                : `https://${sub}.${baseDomain}`;

                              // Create formatted message
                              const message = `🎰 *Account Details* 🎰\n\n` +
                                `👤 *Username:* ${user.username || user.userName || user.userId}\n` +
                                `🔐 *Password:* ${user.password || '***'}\n` +
                                `🌐 *Domain:* ${domain}\n\n` +
                                `_Please keep your credentials safe and secure._`;
                              
                              // Encode message for URL
                              const encodedMessage = encodeURIComponent(message);
                              
                              // Get phone number - try multiple possible field names
                              const phoneNumber = user.phone || user.phoneNumber || user.mobile || user.mobileNumber;
                              
                              // Open WhatsApp with pre-filled message
                              let whatsappUrl;
                              if (phoneNumber) {
                                const cleanPhone = phoneNumber.toString().replace(/\D/g, '');
                                whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
                              } else {
                                whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
                              }
                              
                              window.open(whatsappUrl, '_blank');
                            }}
                            title="Send password via WhatsApp"
                          >
                            <WhatsAppIcon />
                          </button>
                          )}
                        </div>
                      </td>
                      <td className="p-2 border">{user.partnerShip}</td>
                      <td className="p-2 border text-center">{user.matchCommission}</td>
                      <td className="p-2 border text-center">{user.sessionCommission}</td>
                      <td className="p-2 border text-center">{user.casinoCommission || 0}</td>
                      <td className="p-2 border text-center">{user.matkaCommission || 0}</td>
                      <td className="p-2 border">{(user.balance || 0).toFixed(2)}</td>
                      {isDisplayingClients && (
                        <td className="p-2 border">
                          <span
                            className="cursor-pointer text-blue-600 hover:text-blue-800 font-semibold"
                            onClick={function () {
                              setClientExposureModal({
                                isOpen: true,
                                user: user,
                              });
                            }}
                          >
                            {(user.liability || 0).toFixed(2)}
                          </span>
                        </td>
                      )}
                      <td className="p-2 border">
                        <span
                          className={
                            user.isActive
                              ? "text-green-600 font-semibold"
                              : "text-red-600 font-semibold"
                          }
                        >
                          {user.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination - Show when paginationData is provided */}
        {paginationData?.pagination && (
          <div className="flex justify-between items-center p-3 border-t">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Items per page:</span>
              <select
                className="px-2 py-1 border rounded text-sm"
                value={paginationData.itemsPerPage}
                onChange={function (e) {
                  paginationData.setItemsPerPage(Number(e.target.value));
                  paginationData.setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-600">
                Total: {paginationData.pagination.totalUsers || 0}
              </span>
            </div>
            <div className="flex gap-1">
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={paginationData.currentPage === 1}
                onClick={function () {
                  paginationData.setCurrentPage(paginationData.currentPage - 1);
                }}
              >
                &lt;
              </button>
              <button className="px-3 py-1 bg-blue-500 text-white rounded">
                {paginationData.currentPage} / {paginationData.pagination.totalPages || 1}
              </button>
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={paginationData.currentPage >= (paginationData.pagination.totalPages || 1)}
                onClick={function () {
                  paginationData.setCurrentPage(paginationData.currentPage + 1);
                }}
              >
                &gt;
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Dropdown Menu */}
      {activeDropdown && (
        <UserActionsDropdown
          user={transformedUsers.find(function (u) {
            return u.userId === activeDropdown;
          })}
          onAction={handleAction}
          onClose={handleCloseDropdown}
          position={dropdownPosition}
          menuClassName="fixed bg-white rounded shadow-lg z-[9999] py-1 min-w-[180px] list-none m-0 p-0 max-h-[70vh] overflow-y-auto"
          menuStyle={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            boxShadow:
              "0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
          }}
        />
      )}

      {/* Transaction Modal (Deposit/Withdraw) */}
      <TransactionModal
        isOpen={transactionModal.isOpen}
        onClose={function () {
          setTransactionModal({ isOpen: false, type: null, user: null });
        }}
        type={transactionModal.type}
        user={transactionModal.user}
        onSuccess={function () {
          // Refresh data on successful transaction
          console.log('Transaction successful');
        }}
      />

      {/* Partnership Modal */}
      <PartnershipModal
        isOpen={partnershipModal.isOpen}
        onClose={handleClosePartnership}
        userId={formatUserId(partnershipModal.userId)}
        userMongoId={partnershipModal.mongoId}
      />

      {/* Client Exposure Modal */}
      <ClientExposureModal
        isOpen={clientExposureModal.isOpen}
        onClose={function () {
          setClientExposureModal({ isOpen: false, user: null });
        }}
        userId={clientExposureModal.user?.id}
        userName={clientExposureModal.user?.userName}
      />

      {/* Block Action Modal */}
      <BlockActionModal
        isOpen={blockActionModal.isOpen}
        onClose={() => setBlockActionModal({ isOpen: false, user: null })}
        user={blockActionModal.user}
      />
    </div>
  );
};

export default DownlineListPage;
